import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { CUSTOM_EXPORT_FILE_TYPES } from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import {
  EScheduledExportFrequency,
  MAX_DAY_OF_MONTH,
  WEEKDAY_KEYS,
} from "@rentiohq/shared-frontend/dist/reduxV2/scheduledExport";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { JSONSchema6 } from "json-schema";
import {
  EField,
  FREQUENCY_ONCE,
  IExtraData,
  LAST_DAY_OPTION,
} from "./schema.createCustomExport.types";

type TFormValues = { [key: string]: unknown };

type TCustomExportField = JSONSchema6 & {
  showIf?: (formData: TFormValues) => boolean;
  requiredIf?: (formData: TFormValues) => boolean;
  options?: { id: string | number; value: string }[];
};

const MONTHS_PER_YEAR = 12;

const FREQUENCIES = [
  FREQUENCY_ONCE,
  ...Object.values(EScheduledExportFrequency),
];

// 1 to 31 and the last day. A day a month lacks runs on that month's last day.
const DAYS_OF_MONTH: (number | typeof LAST_DAY_OPTION)[] = [
  ...Array.from({ length: MAX_DAY_OF_MONTH }, (_, index) => index + 1),
  LAST_DAY_OPTION,
];

const MONTHS = Array.from({ length: MONTHS_PER_YEAR }, (_, index) => index + 1);

const getDayOfMonthLabel = (day: number | typeof LAST_DAY_OPTION) =>
  day === LAST_DAY_OPTION
    ? getLocalizedText("scheduled_export.run_day.last")
    : `${day}`;

const isFrequency =
  (...frequencies: EScheduledExportFrequency[]) =>
  (formData: TFormValues) =>
    frequencies.includes(
      formData[EField.Frequency] as EScheduledExportFrequency,
    );

const isWeekly = isFrequency(EScheduledExportFrequency.Weekly);
const isYearly = isFrequency(EScheduledExportFrequency.Yearly);
const hasDayOfMonth = isFrequency(
  EScheduledExportFrequency.Monthly,
  EScheduledExportFrequency.Yearly,
);

export const getSchema =
  (initialValues: TFormValues, extraData: IExtraData) =>
  (defaultValues: TFormValues): JSONSchema6 => {
    const properties: { [key: string]: TCustomExportField } = {
      [EField.ExportType]: {
        default: getValue(EField.ExportType, initialValues, defaultValues),
        options: extraData.exportTypes.map(exportType => ({
          id: exportType,
          value: getLocalizedText(
            `export_file.export_type.${exportType}`.toLowerCase(),
          ),
        })),
      },
      [EField.ExportFileType]: {
        default: getValue(EField.ExportFileType, initialValues, defaultValues),
        options: CUSTOM_EXPORT_FILE_TYPES.map(exportFileType => ({
          id: exportFileType,
          value: exportFileType,
        })),
      },
      [EField.Frequency]: {
        default: getValue(EField.Frequency, initialValues, defaultValues),
        options: (extraData.canSchedule ? FREQUENCIES : [FREQUENCY_ONCE]).map(
          frequency => ({
            id: frequency,
            value: getLocalizedText(
              `scheduled_export.frequency.${frequency}`.toLowerCase(),
            ),
          }),
        ),
      },
      [EField.RunWeekday]: {
        default: getValue(EField.RunWeekday, initialValues, defaultValues),
        options: WEEKDAY_KEYS.map((key, index) => ({
          id: index + 1,
          value: getLocalizedText(key),
        })),
        showIf: isWeekly,
        requiredIf: isWeekly,
      },
      [EField.RunMonth]: {
        default: getValue(EField.RunMonth, initialValues, defaultValues),
        options: MONTHS.map(month => ({
          id: month,
          value: getLocalizedText(`system.month.${month}`),
        })),
        showIf: isYearly,
        requiredIf: isYearly,
      },
      [EField.RunDayOfMonth]: {
        default: getValue(EField.RunDayOfMonth, initialValues, defaultValues),
        options: DAYS_OF_MONTH.map(day => ({
          id: day,
          value: getDayOfMonthLabel(day),
        })),
        showIf: hasDayOfMonth,
        requiredIf: hasDayOfMonth,
      },
    };

    return {
      required: [EField.ExportType, EField.ExportFileType, EField.Frequency],
      properties,
    };
  };

export const uiSchema = (extraData: IExtraData): IUiSchema<EField> => ({
  "rentio:navigationTitle": getLocalizedText("export_file.create.title"),
  [EField.ExportType]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("export_file.create.field.export_type"),
    formatSummary: (value: string) =>
      getLocalizedText(`export_file.export_type.${value}`.toLowerCase()),
  },
  [EField.ExportFileType]: {
    "ui:field": ERentioFormField.InlineSelect,
    title: getLocalizedText("export_file.create.field.file_type"),
    isMultiSelect: false,
  },
  [EField.Frequency]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("export_file.create.field.frequency"),
    info: getLocalizedText(
      extraData.canSchedule
        ? "export_file.create.field.frequency.info"
        : "export_file.create.field.frequency.limit_reached",
    ),
    formatSummary: (value: string) =>
      getLocalizedText(`scheduled_export.frequency.${value}`.toLowerCase()),
  },
  [EField.RunWeekday]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("export_file.create.field.run_weekday"),
    formatSummary: (value: number) => getLocalizedText(WEEKDAY_KEYS[value - 1]),
  },
  [EField.RunMonth]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("export_file.create.field.run_month"),
    formatSummary: (value: number) => getLocalizedText(`system.month.${value}`),
  },
  [EField.RunDayOfMonth]: {
    "ui:field": ERentioFormField.DropdownSelect,
    title: getLocalizedText("export_file.create.field.run_day_of_month"),
    info: getLocalizedText("export_file.create.field.run_day_of_month.info"),
    formatSummary: (value: number | typeof LAST_DAY_OPTION) =>
      getDayOfMonthLabel(value),
  },
});
